import { useReportViewContext } from "@/app/features/reports/components/report-viewer-context"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { ChevronDown, X } from 'lucide-react'
import React, { useMemo } from 'react'
import { FilterCounts, FilterLabels, filterNames, Filters, FilterTypes, SelectedFilters } from '../types/filters'
import extractFilterCount from '../utils/extractFilterCount'
import { getSelectedSpaces, groupSelectedSpaces } from './filter-drawer'
import { FilterItem } from '../utils/entities'

// Helper function to find a FilterItem by ID
function findFilterItemById(items: FilterItem[] | undefined, id: string): FilterItem | null {
    if (!items) return null;
    for (const item of items) {
        if (item.id === id) {
            return item;
        }
        if (item.children) {
            const found = findFilterItemById(item.children, id);
            if (found) {
                return found;
            }
        }
    }
    return null;
}

type SpaceFilterPillProps = {
    category: string;
    selected: string;
    filters: Filters;
    onRemove: (category: FilterTypes, filter: string) => void;
    onRemoveAll: (category: FilterTypes) => void;
    filterCounts: FilterCounts;
}

const SpacesFilterDropdown: React.FC<SpaceFilterPillProps> = ({
    selected,
    category,
    filters,
    onRemove,
    onRemoveAll,
    filterCounts
}) => {
    const originalCategoryData = filters[category as FilterTypes] ?? [];
    const selectedIds = useMemo(() => new Set(selected.split(',').filter(id => id)), [selected]); // Use a Set for efficient lookup

    // Calculate total selected count based on the Set size
    const totalFilters = selectedIds.size;

    if (totalFilters === 0) {
        return <p className="text-sm text-muted-foreground">No items selected</p>;
    }

    // Find explicitly selected items with their full path/name for display
    const selectedItemsDetails = useMemo(() => 
        getSelectedSpaces(originalCategoryData, Array.from(selectedIds))
    , [originalCategoryData, selectedIds]);

    // Group selected items for display structure (useful for knowing parent names)
    const groupedSelected = useMemo(() => 
        groupSelectedSpaces(selectedItemsDetails)
    , [selectedItemsDetails]);

    // Calculate the count based on display logic
    const displayItemCount = useMemo(() => {
        let count = 0;
        (originalCategoryData ?? []).forEach(topLevelItem => {
            if (selectedIds.has(topLevelItem.id)) {
                count += 1; // Count the consolidated parent row
            } else if (topLevelItem.children) {
                const selectedChildren = topLevelItem.children.filter(child => selectedIds.has(child.id));
                count += selectedChildren.length; // Count individual selected child rows
            }
        });
        return count;
    }, [originalCategoryData, selectedIds]);

    // Helper to remove all descendant IDs of a given parent ID
    const handleRemoveParentAndDescendants = (parentId: string) => {
        const parentItem = findFilterItemById(originalCategoryData, parentId);
        const idsToRemove = new Set<string>([parentId]);
        const collectDescendantIds = (items: FilterItem[]) => {
            for (const item of items) {
                idsToRemove.add(item.id);
                if (item.children) {
                    collectDescendantIds(item.children);
                }
            }
        };
        if (parentItem?.children) {
            collectDescendantIds(parentItem.children);
        }
        idsToRemove.forEach(id => {
             // Check if the ID was actually selected before trying to remove
             if (selectedIds.has(id)) { 
                onRemove(category as FilterTypes, id);
             }
        });
    };

    // Function to render a single item in the dropdown
    const renderDropdownItem = (item: {id: string, name: string}, count: number, displayText: string) => (
        <DropdownMenuItem key={item.id} className="flex justify-between items-center">
            <span>{displayText}</span>
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">({count || 0})</span>
                <X
                    className="h-3.5 w-3.5 cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // If removing a parent shown consolidated, remove all descendants
                        const originalItem = findFilterItemById(originalCategoryData, item.id);
                        if (originalItem?.children && selectedIds.has(item.id)) {
                            handleRemoveParentAndDescendants(item.id);
                        } else {
                            onRemove(category as FilterTypes, item.id);
                        }
                    }}
                />
            </div>
        </DropdownMenuItem>
    );

    return (
        <div className="flex items-center border rounded-md h-9 overflow-hidden">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-full flex items-center gap-1 px-3 rounded-none border-r">
                        <span>{FilterLabels[category as FilterTypes]}</span>
                        <Badge variant="secondary" className="ml-1 px-1.5 font-normal bg-muted-foreground/50">
                            {/* Use the new displayItemCount */}
                            {displayItemCount}
                        </Badge>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[280px]">
                    <DropdownMenuLabel>Active Filters</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {
                        // Iterate through the *original* data structure at the top level
                        originalCategoryData.map((topLevelItem, index) => {
                            // Check if the top-level item itself is selected
                            if (selectedIds.has(topLevelItem.id)) {
                                // Render the consolidated top-level item
                                const count = extractFilterCount(filterCounts, topLevelItem, 'filtered');
                                return (
                                    <React.Fragment key={topLevelItem.id}>
                                     {index > 0 && <DropdownMenuSeparator />} 
                                     {/* No label needed if rendering parent */}
                                     {renderDropdownItem(topLevelItem, count, topLevelItem.name)}
                                    </React.Fragment>
                                );
                            } else if (topLevelItem.children) {
                                // If top-level not selected, check its children
                                const selectedChildren = topLevelItem.children.filter(child => selectedIds.has(child.id));
                                if (selectedChildren.length > 0) {
                                    // If some children are selected, render them under a label
                                    return (
                                        <React.Fragment key={`${topLevelItem.id}-children`}>
                                            {index > 0 && <DropdownMenuSeparator />}
                                            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                                                {topLevelItem.name}
                                            </DropdownMenuLabel>
                                            {selectedChildren.map((child) => {
                                                const count = extractFilterCount(filterCounts, child, 'filtered');
                                                // Find the full path name from groupedSelected for better display?
                                                // This part is tricky - how to get the nice path like "Parent > Child"?
                                                // Maybe use find in selectedItemsDetails?
                                                const detail = selectedItemsDetails.find(d => d.id === child.id);
                                                const nameParts = detail?.name?.split(' > ') ?? [child.name];
                                                const displayChildName = nameParts[nameParts.length - 1];

                                                return renderDropdownItem(child, count, displayChildName );
                                            })}
                                        </React.Fragment>
                                    );
                                }
                            }
                            return null; // Don't render anything if top-level and its children are not selected
                        })
                    }
                </DropdownMenuContent>
            </DropdownMenu>
            <Button
                variant="ghost"
                size="icon"
                className="h-full w-6 rounded-none text-muted-foreground hover:text-foreground hover:bg-muted/50"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemoveAll(category as FilterTypes);
                }}
                aria-label={`Remove all ${FilterLabels[category as FilterTypes]} filters`}
            >
                <X className="h-3.5 w-3.5" />
            </Button>
        </div>
    );
};

interface FilterFacetsProps {
    exposedFacet?: FilterTypes | null;
    // /filters: { [key in FilterTypes]?: string[] };
    defaultImplicitFilters?: { [key in FilterTypes]?: string[] };
    removeFilter: (category: FilterTypes, value: string) => void;
    selectedFilters: SelectedFilters;
}

export const FilterFacets: React.FC<FilterFacetsProps> = ({
    exposedFacet,
    // filters,
    defaultImplicitFilters,
    removeFilter,
    selectedFilters
}) => {
    const { filters: { filteredCounts, facets: filters } } = useReportViewContext();

    // Guard clause: If selectedFilters is not ready, render nothing
    if (!selectedFilters) return null;

    // Group filters by category for better organization
    const categorizedFilters = Object.entries(selectedFilters).reduce((acc, [category, categoryFilters]) => {
        if (Array.isArray(categoryFilters) && categoryFilters.length > 0) {
            acc[category] = categoryFilters;
        }
        return acc;
    }, {} as Record<string, string[]>);

    // Filter out the exposed facet
    const filteredEntries = Object.entries(categorizedFilters).filter(
        ([category]) => category !== exposedFacet
    );

    // Handler to remove all filters for a specific category
    const handleRemoveAllByCategory = (categoryToRemove: FilterTypes) => {
        const filtersToRemove = selectedFilters[categoryToRemove] || [];
        filtersToRemove.forEach(filter => removeFilter(categoryToRemove, filter));
    };

    // Determine if the default excluded filter should be shown
    const showDefaultExcluded = defaultImplicitFilters?.excluded?.includes('included') &&
        (!selectedFilters.excluded || selectedFilters.excluded.length === 0);

    // Prepare explicit filters, filtering out the exposed facet
    const explicitFilters = Object.entries(selectedFilters)
        .filter(([category, categoryFilters]) => category !== exposedFacet && categoryFilters.length > 0)
        .flatMap(([category, categoryFilters]) => {
            // // Handle 'excluded' category specifically with FilterPill
            // if (category === 'excluded') {
            //     return categoryFilters.map(filter => (
            //         <FilterPill
            //             key={`${category}:${filter}`}
            //             text={`${FilterLabels[category as FilterTypes] || category}: ${filter === 'included' ? 'Included' : filter}`}
            //             filter={filter}
            //             count={filteredCounts?.tests?.[filter as keyof typeof filteredCounts.tests] || 0}
            //             onClick={() => removeFilter(category as FilterTypes, filter)}
            //         />
            //     ));
            // }
            // Handle other known hierarchical filters (like 'space') with SpacesFilterDropdown
            //else
            if (filterNames.includes(category as FilterTypes)) {
                return (
                    <SpacesFilterDropdown
                        key={category}
                        category={category}
                        selected={categoryFilters.join(',')}
                        filters={filters}
                        onRemove={removeFilter}
                        onRemoveAll={handleRemoveAllByCategory}
                        filterCounts={filteredCounts}
                    />
                );
            }
            // Fallback for any other unexpected categories (render as simple pills)
            else {
                return categoryFilters.map(filter => (
                    <FilterPill
                        key={`${category}:${filter}`}
                        text={`${FilterLabels[category as FilterTypes] || category}: ${filter}`}
                        filter={filter}
                        count={filteredCounts[category]?.[filter] || 0}
                        onClick={() => removeFilter(category as FilterTypes, filter)}
                    />
                ));
            }
        });

    return (
        <div className="flex flex-wrap gap-2 items-center">
            {/* Render the static default pill if applicable */}
            {showDefaultExcluded && exposedFacet !== 'excluded' && (
                <StaticDefaultPill text="Showing: Included Tests (Default)" />
            )}

            {/* Render the explicit filter pills/dropdowns */}
            {explicitFilters}
        </div>
    );
};

// Define a simple component for the static default pill
const StaticDefaultPill: React.FC<{ text: string }> = ({ text }) => {
    return (
        <Badge
            variant="secondary"
            className="flex items-center gap-1 text-sm px-3 py-1"
        >
            <span>{text}</span>
        </Badge>
    );
};

// Keep the FilterPill component for non-space filters
const FilterPill = ({ text, filter, count, onClick }) => {
    return (
        <Badge
            variant="secondary"
            className="flex items-center gap-1 text-sm px-3 py-1"
        >
            <span>{text}</span>
            <span className="text-muted-foreground ml-1">({count || 0})</span>
            <X
                className="h-3 w-3 cursor-pointer ml-1"
                onClick={() => onClick(filter)}
            />
        </Badge>
    );
};

export default FilterFacets;

